import { DOCUMENT, isPlatformBrowser } from '@angular/common';
import {
  Component,
  ChangeDetectionStrategy,
  HostListener,
  Signal,
  computed,
  effect,
  ViewEncapsulation,
  CUSTOM_ELEMENTS_SCHEMA,
  inject,
  PLATFORM_ID,
} from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { map } from 'rxjs/operators';

import { AppService } from '@app/app.service';
import { BreadcrumbService } from '@modules/shared/services/breadcrumb.service';
import { DataLayerService } from '@modules/shared/services/data-layer.service';

import { LegalPage, LegalPageKey } from './legal.adapter';
import { LegalService } from './legal.service';
import { SettingComponent } from './setting.component';

@Component({
  selector: 'mb-legal',
  templateUrl: './legal.component.html',
  styleUrls: ['./legal.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  imports: [RouterLink, SettingComponent],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class LegalComponent {
  private legalService = inject(LegalService);
  private router = inject(Router);
  private route = inject(ActivatedRoute);
  private appService = inject(AppService);
  private breadcrumbService = inject(BreadcrumbService);
  private dataLayerService = inject(DataLayerService);
  private platformId = inject(PLATFORM_ID);
  private document = inject(DOCUMENT);

  public legalPages: Signal<LegalPage[]>;
  public currentSlug: Signal<LegalPageKey>;
  public currentPage: Signal<LegalPage | undefined>;

  private _fragment: Signal<string | null>;

  @HostListener('click', ['$event'])
  onClick(event: Event) {
    const path = event.composedPath() as Array<any>;
    const anchor = path.find((p) => p.tagName?.toLowerCase() === 'a');
    // enable anchor scroll navigation within legal pages
    if (anchor && anchor.getAttribute('href') === '#CCPA') {
      this.router.navigate([], { fragment: 'CCPA' });
      event.preventDefault();
      event.stopPropagation();
    }
  }

  constructor() {
    this.appService.lightBackgroundPage();

    // signals
    this.legalPages = toSignal(this.legalService.legalPages$.pipe(takeUntilDestroyed()), { initialValue: [] });
    this.currentSlug = toSignal(
      this.route.params.pipe(
        takeUntilDestroyed(),
        map((params) => params.slug)
      )
    );
    this._fragment = toSignal(this.route.fragment.pipe(takeUntilDestroyed()), { initialValue: null });

    // computed properties
    this.currentPage = computed(() => this.legalPages().find((page) => page.slug === this.currentSlug()));

    // effects
    this.pageTitleEffect();
    this.pageBreadcrumbEffect();
    this.scrollToFragmentEffect();
    this.slugNotFoundEffect();
    this.pageViewEffect();
  }

  private pageTitleEffect() {
    effect(() => {
      const page = this.currentPage();
      page && this.appService.setTitle(`${page.title} | Mercedes-Benz Media`);
    });
  }

  private pageBreadcrumbEffect() {
    effect(() => {
      const page = this.currentPage();
      if (page) {
        // IMPROVE: race condition with navigation end event in breadcrumb service
        this.route.snapshot.data.breadcrumb = {
          label: page.title,
          link: page.slug,
        };
        this.breadcrumbService.rebuildBreadcrumbs();
      }
    });
  }

  private scrollToFragmentEffect() {
    effect(() => {
      const fragment = this._fragment();
      if (this.currentPage() && fragment && isPlatformBrowser(this.platformId)) {
        setTimeout(() => {
          const element = this.document.getElementById(fragment);
          if (element) {
            window.scrollTo({ top: element.offsetTop - 50, left: 0, behavior: 'smooth' });
          }
        }, 200);

        // reset fragment
        this.router.navigate([]);
      }
    });
  }

  private slugNotFoundEffect() {
    effect(() => {
      if (
        this.legalPages().length &&
        this.currentSlug() &&
        this.legalPages().find((page) => page.slug === this.currentSlug()) === undefined
      ) {
        // requested slug is not found in the list of legal pages
        this.router.navigate(['404'], { skipLocationChange: true });
      }
    });
  }

  private pageViewEffect() {
    effect(() => {
      const page = this.currentPage();
      if (page) {
        this.dataLayerService.pushPageView({
          id: page.id,
          name: page.title,
          type: 'legal_page',
          publishDate: undefined,
          marsOriginId: undefined,
        });
      }
    });
  }
}
